import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import CustomLink from '../CustomLink';
import { Icon } from '../Icon';
import { PhoneSupportNotice } from '../PhoneSupportNotice';
import { ContactSupportForm } from '../ContactSupportForm';
import { CallMeBackForm } from '../CallMeBackForm';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../../shared/links';
import { useUser } from '../../shared/use-user';
import styles from './DashboardContactBlock.css';

const cx = classnames.bind(styles);

const LINK_OPTIONS = [
    {
        id: 'call',
        title: 'Call us',
        description: 'Click to call. We answer right away.',
        link: `tel:${SUPPORT_PHONE}`,
        target: 'custom',
        eventType: EVENT_TYPES.SUPPORT_PHONE_CLICKED,
        isPhoneCall: true
    },
    {
        id: 'email',
        title: 'Email us',
        description: 'Send us a message and we will get back to you in 15 minutes.',
        link: `mailto:${SUPPORT_EMAIL}`,
        target: 'custom',
        eventType: EVENT_TYPES.SUPPORT_EMAIL_CLICKED
    }
];

function DashboardContactBlock({ clickBlock, isUrgent }) {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isCallMeBackOpen, setIsCallMeBackOpen] = useState(false);
    const { userData } = useUser();
    const title = 'Need help? Contact us';
    const description = 'Tell us what happened and our security specialist will help you with the next steps.';

    const openForm = () => {
        logEvent(EVENT_TYPES.SUPPORT_WIDGET_CLICKED, { 'target_click_block': clickBlock });
        setIsFormOpen(true);
    };

    return (
        <>
            <section
                className={styles.root}
                data-viewed-event={EVENT_TYPES.DASHBOARD_CONTACT_BLOCK_VIEWED}
            >
                <div className={styles.header}>
                    <h2 className={cx('title', { titleUrgent: isUrgent })}>{title}</h2>
                    <p className={styles.description}>{description}</p>
                </div>
                <ul className={styles.list}>
                    {LINK_OPTIONS.map(item => (
                        <li key={item.id}>
                            <CustomLink
                                to={item.link}
                                target={item.target}
                                className={styles.link}
                                onClick={() => logEvent(item.eventType, { 'target_click_block': clickBlock })}
                            >
                                <div className={styles.titleRow}>
                                    <span className={styles.itemTitle}>{item.title}</span>
                                    <Icon name='chevronRightFilled' className={styles.arrow} aria-hidden='true' />
                                </div>
                                <span className={styles.itemSubtitle}>{item.description}</span>
                                {item.isPhoneCall && <PhoneSupportNotice />}
                            </CustomLink>
                        </li>
                    ))}
                    <li>
                        <button
                            type='button'
                            className={styles.link}
                            onClick={() => {
                                logEvent(EVENT_TYPES.CALL_ME_BACK_CLICKED, { 'target_click_block': clickBlock });
                                setIsCallMeBackOpen(true);
                            }}
                        >
                            <div className={styles.titleRow}>
                                <span className={styles.itemTitle}>Call me back</span>
                                <Icon name='chevronRightFilled' className={styles.arrow} aria-hidden='true' />
                            </div>
                            <span className={styles.itemSubtitle}>Leave your number and we will call you back.</span>
                            <PhoneSupportNotice />
                        </button>
                    </li>
                    <li>
                        <button
                            type='button'
                            className={styles.link}
                            onClick={openForm}
                        >
                            <div className={styles.titleRow}>
                                <span className={styles.itemTitle}>Send us a message</span>
                                <Icon name='chevronRightFilled' className={styles.arrow} aria-hidden='true' />
                            </div>
                            <span className={styles.itemSubtitle}>Fill out a short form and our team will guide you step by step.</span>
                        </button>
                    </li>
                </ul>
            </section>
            {isFormOpen && (
                <ContactSupportForm
                    onClose={() => setIsFormOpen(false)}
                    email={userData.email}
                />
            )}
            {isCallMeBackOpen && (
                <CallMeBackForm
                    onClose={() => setIsCallMeBackOpen(false)}
                    email={userData.email}
                />
            )}
        </>
    );
}

DashboardContactBlock.propTypes = {
    clickBlock: PropTypes.string.isRequired,
    isUrgent: PropTypes.bool
};

DashboardContactBlock.defaultProps = {
    isUrgent: false
};

export {
    DashboardContactBlock
};
